// ─────────────────────────────────────────────────────────────────────────────
// VIP Early-Access scarcity banner + waitlist capture.
//
// Behaviour matrix (driven by /api/promo/status/CALM100):
//   • remaining > 15  → render nothing (silent)
//   • 1 <= remaining <= 15 → soft-orange urgency banner "Only N left"
//   • remaining <= 0 → red-tinged "program full" banner + inline email
//     capture that posts to /api/promo/waitlist
//
// Deliberately NOT using an <alert> role so screen readers don't spam
// users at every 30s poll. The message is still readable and the CTA
// buttons carry data-testids.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from "react";
import axios from "axios";
import { AlertCircle, Sparkles, Check } from "lucide-react";
import { usePromoStatus } from "../lib/usePromoStatus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CODE = "CALM100";
const URGENCY_THRESHOLD = 15;

export const PromoScarcityBanner = ({ variant = "hero" }) => {
  const { status } = usePromoStatus(CODE);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  if (!status || !status.exists) return null;
  const { remaining, is_full } = status;

  // Below threshold → don't clutter the marketing page.
  if (!is_full && remaining > URGENCY_THRESHOLD) return null;

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post(`${API}/promo/waitlist`, { code: CODE, email: email.trim() });
      setSubmitted(true);
    } catch (err) {
      setSubmitError("Something went wrong — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Style variants: `hero` is the huge landing banner, `compact` is the
  // slimmer version we drop above the signup form.
  const wrapper = variant === "hero"
    ? "rounded-2xl px-6 py-5 sm:px-8"
    : "rounded-xl px-4 py-3";
  const heading = variant === "hero"
    ? "text-lg sm:text-xl font-semibold"
    : "text-sm sm:text-base font-semibold";
  const sub = variant === "hero" ? "text-sm mt-1" : "text-xs mt-0.5";

  if (is_full) {
    return (
      <div
        className={`${wrapper} bg-gradient-to-r from-[#FEE2E2] via-[#FEF3C7] to-[#FEE2E2] border border-[#E11D48]/25 text-[#7F1D1D]`}
        data-testid="promo-full-banner"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0 text-[#E11D48]" />
          <div className="flex-1">
            <div className={heading} data-testid="promo-full-title">
              Our early access program is now full.
            </div>
            <div className={`${sub} text-[#9F1239]`}>
              Sign up below for a standard account, or leave your email — we'll ping you if a spot opens up.
            </div>
          </div>
        </div>
        {!submitted ? (
          <form
            onSubmit={handleWaitlist}
            className="mt-3 flex flex-col sm:flex-row gap-2"
            data-testid="promo-waitlist-form"
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="flex-1 min-w-0 rounded-full px-4 py-2 text-sm bg-white border border-[#E11D48]/30 text-[#1B2349] placeholder:text-[#9F1239]/60 focus:outline-none focus:border-[#E11D48]"
              data-testid="promo-waitlist-email-input"
            />
            <button
              type="submit"
              disabled={submitting}
              className="rounded-full px-5 py-2 text-sm font-medium bg-[#1B2349] text-white hover:bg-[#0F1838] active:scale-[0.98] disabled:opacity-60 transition"
              data-testid="promo-waitlist-submit-btn"
            >
              {submitting ? "Adding…" : "Notify me"}
            </button>
          </form>
        ) : (
          <div
            className="mt-3 flex items-center gap-2 text-sm text-[#065F46]"
            data-testid="promo-waitlist-success"
          >
            <Check className="w-4 h-4" /> You're on the list. We'll email if a spot frees up.
          </div>
        )}
        {submitError && (
          <div className="mt-2 text-xs text-[#7F1D1D]">{submitError}</div>
        )}
      </div>
    );
  }

  // 1 ≤ remaining ≤ 15 — soft orange scarcity.
  return (
    <div
      className={`${wrapper} bg-gradient-to-r from-[#FFEDD5] to-[#FDE68A] border border-[#F59E0B]/40 text-[#7C2D12] flex items-start gap-3`}
      data-testid="promo-scarcity-banner"
    >
      <Sparkles className="w-5 h-5 flex-shrink-0 text-[#F59E0B] mt-0.5" />
      <div>
        <div className={heading} data-testid="promo-scarcity-title">
          Hurry! Only <span data-testid="promo-remaining-count">{remaining}</span> early access {remaining === 1 ? "spot" : "spots"} remaining.
        </div>
        <div className={`${sub} text-[#9A3412]`}>
          Use code <span className="font-mono font-semibold tracking-wider">CALM100</span> at signup for lifetime All Access — free, forever.
        </div>
      </div>
    </div>
  );
};

export default PromoScarcityBanner;
